import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Terminal, Menu, X as XIcon, PenLine } from 'lucide-react'
import { Link } from 'react-router-dom'
import { NAV } from '../constants/testIds'

const navLinks = [
  { label: 'Work', id: 'work', scroll: true },
  { label: 'Journey', id: 'timeline', scroll: true },
  { label: 'Services', id: 'services', scroll: true },
  { label: 'Skills', id: 'skills', scroll: true },
  { label: 'Contact', id: 'contact', scroll: true },
]

export default function Navigation({ onCommandOpen, onNavigate }) {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const handleNav = (id) => {
    onNavigate(id)
    setMobileOpen(false)
  }

  return (
    <>
      <motion.header
        data-testid={NAV.root}
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || mobileOpen
            ? 'bg-white/90 backdrop-blur-xl backdrop-saturate-150 border-b border-black/6 shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <ScrollProgress />
        <div className="max-w-7xl mx-auto px-5 md:px-12 h-14 flex items-center justify-between">

          {/* Logo */}
          <button
            data-testid={NAV.logo}
            onClick={() => handleNav('hero')}
            aria-label="Go to top"
            className="font-mono text-sm font-semibold tracking-[0.15em] text-[#111111] hover:text-[#0047AB] transition-colors duration-200"
          >
            <span className="text-[#0047AB]">› </span>AR
          </button>

          {/* Desktop nav links */}
          <nav className="hidden md:flex items-center gap-7" aria-label="Main navigation">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => handleNav(link.id)}
                className="font-body text-sm text-[#6B7280] hover:text-[#111111] transition-colors duration-200 relative group"
              >
                {link.label}
                <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-[#0047AB] group-hover:w-full transition-all duration-300" />
              </button>
            ))}
            <Link to="/blog"
              className="font-body text-sm text-[#6B7280] hover:text-[#111111] transition-colors duration-200 relative group flex items-center gap-1">
              <PenLine size={12} className="text-[#0047AB]" />
              Blog
              <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-[#0047AB] group-hover:w-full transition-all duration-300" />
            </Link>
          </nav>

          {/* Right — desktop */}
          <div className="hidden md:flex items-center gap-3">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs text-emerald-700 font-mono border border-emerald-200 bg-emerald-50">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Available
            </div>
            <button
              data-testid={NAV.commandBtn}
              onClick={onCommandOpen}
              aria-label="Open command palette (⌘K)"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#E5E7EB] bg-white hover:border-[#0047AB]/40 hover:bg-[#EBF2FF] transition-all duration-200 group shadow-sm"
            >
              <Terminal size={11} className="text-[#9CA3AF] group-hover:text-[#0047AB] transition-colors" />
              <span className="font-mono text-xs text-[#9CA3AF] group-hover:text-[#0047AB] transition-colors">⌘K</span>
            </button>
          </div>

          {/* Mobile right: Available badge (tiny) + hamburger */}
          <div className="flex md:hidden items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500" title="Available" />
            <button
              onClick={() => setMobileOpen(v => !v)}
              aria-label="Open menu"
              aria-expanded={mobileOpen}
              className="p-2 rounded-lg border border-[#E5E7EB] bg-white shadow-sm"
            >
              {mobileOpen ? <XIcon size={16} className="text-[#374151]" /> : <Menu size={16} className="text-[#374151]" />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="md:hidden overflow-hidden bg-white border-b border-[#E5E7EB]"
              data-testid="mobile-nav-menu"
            >
              <nav className="px-5 py-4 flex flex-col gap-1" aria-label="Mobile navigation">
                {navLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => handleNav(link.id)}
                    className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-left text-[#374151] font-body text-sm hover:bg-[#F3F4F6] hover:text-[#111111] transition-colors duration-150"
                  >
                    {link.label}
                    <span className="text-[#9CA3AF]">→</span>
                  </button>
                ))}
                <Link to="/blog" onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-[#374151] font-body text-sm hover:bg-[#F3F4F6] hover:text-[#111111] transition-colors duration-150">
                  <span className="flex items-center gap-2"><PenLine size={13} className="text-[#0047AB]" />Blog</span>
                  <span className="text-[#9CA3AF]">→</span>
                </Link>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>
    </>
  )
}

function ScrollProgress() {
  const [progress, setProgress] = useState(0)
  useEffect(() => {
    const update = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight
      setProgress(max > 0 ? (window.scrollY / max) * 100 : 0)
    }
    window.addEventListener('scroll', update, { passive: true })
    return () => window.removeEventListener('scroll', update)
  }, [])
  return (
    <div className="absolute bottom-0 left-0 right-0 h-px bg-[#E5E7EB]">
      <div className="h-full bg-[#0047AB] transition-all duration-100" style={{ width: `${progress}%` }} />
    </div>
  )
}
